// Lightweight singleton loader for the Google Maps JavaScript API.
// - Reads the key via getGoogleMapsBrowserKey() (BROWSER_KEY, then legacy).
// - Loads Maps JS v=weekly with `geometry,places,drawing` libraries so the
//   Iter B/C measurement workspace can compute areas/lengths and use
//   autocomplete without a second script load. `drawing` is included only
//   because Terra Draw currently probes for it; we do NOT use the deprecated
//   google.maps.drawing.DrawingManager for measurements.
// - Guarantees the script is loaded exactly once across component mounts.
// - The key value is never logged, never returned in any error object, and
//   the URL is only assembled in-browser.

import { getGoogleMapsBrowserKey } from "./googleMapsKey";

let promise = null;

// Back-compat named export — existing StreetViewPanel imports this. It now
// resolves through the new resolver (BROWSER_KEY preferred, legacy fallback).
export function getGoogleMapsKey() {
  return getGoogleMapsBrowserKey();
}

export function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.reject(new Error("no window"));
  if (window.google && window.google.maps) return Promise.resolve(window.google);
  if (promise) return promise;
  const key = getGoogleMapsBrowserKey();
  if (!key) return Promise.reject(new Error("MISSING_KEY"));

  promise = new Promise((resolve, reject) => {
    const cbName = "__leadforge_gmaps_init__";
    window[cbName] = () => resolve(window.google);
    let resolved = false;
    const origResolve = resolve;
    resolve = (v) => {
      resolved = true;
      origResolve(v);
    };
    window.gm_authFailure = () => {
      if (!resolved) reject(new Error("AUTH_FAILURE"));
    };
    const s = document.createElement("script");
    s.src =
      "https://maps.googleapis.com/maps/api/js" +
      `?key=${encodeURIComponent(key)}` +
      "&libraries=geometry,places,drawing" +
      `&callback=${cbName}` +
      "&v=weekly";
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error("SCRIPT_LOAD_ERROR"));
    document.head.appendChild(s);
  }).catch((e) => {
    promise = null;
    throw e;
  });
  return promise;
}
